import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'

const TYPE_NAME_MAX = 128

interface Props {
  readonly open: boolean
  readonly onClose: () => void
  readonly onSubmit: (
    type_name: string,
    is_axes: boolean
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

/**
 * CustomDataTypeFormModal — Add only.
 *
 * Renames happen inline on each card (matches the Ridgegap pattern,
 * keeps the modal focused on the one-shot create flow). Single field
 * keeps the form small and the Modal short.
 */
export function CustomDataTypeFormModal({ open, onClose, onSubmit }: Props): JSX.Element {
  const [typeName, setTypeName] = useState('')
  const [isAxes, setIsAxes] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setTypeName('')
      setIsAxes(false)
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open])

  const handleSubmit = async (): Promise<void> => {
    const trimmed = typeName.trim()
    if (trimmed.length < 1) {
      setFieldError('Name is required')
      return
    }
    if (trimmed.length > TYPE_NAME_MAX) {
      setFieldError(`Name must be ≤${TYPE_NAME_MAX} chars`)
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const result = await onSubmit(trimmed, isAxes)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f === 'type_name') {
      setFieldError(result.message)
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add custom data type"
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
        <label className="block">
          <span className="block text-body-sm font-body text-text-primary mb-1">
            Name
          </span>
          <Input
            value={typeName}
            hasError={fieldError !== null}
            onChange={(e) => setTypeName(e.target.value)}
            placeholder="e.g. Color"
            maxLength={TYPE_NAME_MAX}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleSubmit()
              }
            }}
          />
          {fieldError !== null ? (
            <span className="block text-body-xs font-body text-terra mt-1">
              {fieldError}
            </span>
          ) : (
            <span className="block text-body-xs font-body text-text-secondary mt-1">
              The values come next — add them after the type is created.
            </span>
          )}
        </label>

        <Checkbox
          checked={isAxes}
          onChange={(e) => setIsAxes(e.target.checked)}
          label="Is axes"
        />
      </div>
    </Modal>
  )
}
